/**
 * TNCY-9 §b — `useOwnerMemberContractPositions` merged owner/member list for
 * the Contract Positions page.
 *
 * Mirrors the Contracts `useOwnerMemberContracts` (TNCY-6b) but for the
 * Contract Positions list, which the BE extended with `scope=member` +
 * ownership metadata (`ownership`/`ownerTenantName`/`role`) on
 * `ContractPositionListItem` in projects/v1@2.6.0 (TNCY-8). Member rows are
 * positions of contracts the caller was granted cross-tenant read access to
 * via a `project_contractor` / `contract_contractor` grant — the BE already
 * projects them to the caller's role surface (XT-D22) and omits out-of-
 * surface rows (a customer / reporting_subcontractor sees none, AC-3), so the
 * FE just renders what it gets.
 *
 * It fires BOTH scopes and merges them into one table (Christian 2026-06-23 —
 * ONE table, not tabs) via the shared `mergeOwnerMember` primitive:
 *
 *   - `scope=own`     — the caller's own positions, SERVER-paginated.
 *   - `scope=member`  — the cross-tenant fan-out of GRANTED positions, loaded
 *                       in FULL but BOUNDED (one page at `MEMBER_PAGE_SIZE`).
 *
 * Own-only fast path: when there are no member rows the hook returns the own
 * query's items + its server `total` verbatim, so a tenant with no
 * cross-tenant grants sees exactly the pre-TNCY-9 list.
 */
import { useMemo } from "react";
import { mergeOwnerMember, MEMBER_PAGE_SIZE } from "@aeriosiq/ownership";
import { useContractPositionsListQuery } from "./useContractPositions.js";
import type {
  ContractPositionListItem,
  ContractPositionsSortDir,
  ContractPositionsSortKey,
  ListContractPositionsParams,
} from "./types.js";

export interface OwnerMemberContractPositionsResult {
  rows: ContractPositionListItem[];
  total: number;
  hasMemberRows: boolean;
  memberTruncated: boolean;
  isPending: boolean;
  isError: boolean;
}

function compareBy(
  key: ContractPositionsSortKey,
  dir: ContractPositionsSortDir,
): (a: ContractPositionListItem, b: ContractPositionListItem) => number {
  const sign = dir === "asc" ? 1 : -1;
  return (a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case "positionCode":
        av = a.positionCode;
        bv = b.positionCode;
        break;
      case "projectName":
        av = a.projectName;
        bv = b.projectName;
        break;
      case "siteName":
        av = a.siteName ?? "";
        bv = b.siteName ?? "";
        break;
      case "assetDisplayLabel":
        av = a.assetDisplayLabel;
        bv = b.assetDisplayLabel;
        break;
      case "state":
        av = a.state;
        bv = b.state;
        break;
      default:
        av = a.positionCode;
        bv = b.positionCode;
    }
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv)) * sign;
  };
}

/**
 * @param params  the page's list params (page/pageSize/sort/filters). The
 *                hook derives the own + member queries from it; do NOT pass
 *                `scope` — it's set internally.
 */
export function useOwnerMemberContractPositions(
  params: ListContractPositionsParams,
): OwnerMemberContractPositionsResult {
  const ownParams = useMemo<ListContractPositionsParams>(
    () => ({ ...params, scope: "own" }),
    [params],
  );
  const own = useContractPositionsListQuery(ownParams);

  const memberParams = useMemo<ListContractPositionsParams>(
    () => ({
      ...params,
      scope: "member",
      page: 1,
      pageSize: MEMBER_PAGE_SIZE,
    }),
    [params],
  );
  const member = useContractPositionsListQuery(memberParams);

  const sortKey = params.sortBy ?? "positionCode";
  const sortDir = params.sortDir ?? "desc";

  return useMemo<OwnerMemberContractPositionsResult>(() => {
    const memberItems = member.data?.items ?? [];
    // Member query failing is non-fatal — own remains the spine.
    const merged = mergeOwnerMember<ContractPositionListItem>({
      ownItems: own.data?.items ?? [],
      ownTotal: own.data?.total ?? 0,
      memberItems,
      memberTotal: member.data?.total ?? memberItems.length,
      getId: (r) => r.contractPositionId,
      compare: compareBy(sortKey, sortDir),
    });
    return {
      ...merged,
      isPending: own.isPending,
      isError: own.isError,
    };
  }, [own.data, member.data, own.isPending, own.isError, sortKey, sortDir]);
}
